// "Activá los avisos de tus pedidos" (decisión D53): Web Push con las mismas claves VAPID del sistema. Llegan los
// mensajes de la distribuidora y los cambios de estado de sus pedidos, aunque la tienda esté cerrada. Una suscripción
// por celular y sesión; el service worker (public/sw-push.js) las muestra y, al tocarlas, abre la pantalla.
import { borrar, guardar, leer } from './almacen';
import { api } from './api';
import { claveLocal } from './tienda-actual';

export type EstadoPush =
  /** El navegador no tiene notificaciones push (o la tienda corre sin service worker). */
  | 'no_soportado'
  /** iPhone: hace falta agregar la tienda a la pantalla de inicio. */
  | 'iphone_instalar'
  /** El servidor no tiene claves de push. */
  | 'sin_servidor'
  /** Se negó el permiso en el navegador. */
  | 'bloqueado'
  | 'apagado'
  | 'activo';

const APAGADO = claveLocal('push-apagado');
const REGISTRADO = claveLocal('push-registrado');

const esIphone = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
const instalada = () =>
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
const soporta = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function registro(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

function bytesDeClave(base64: string): Uint8Array<ArrayBuffer> {
  const relleno = '='.repeat((4 - (base64.length % 4)) % 4);
  const b = atob((base64 + relleno).replace(/-/g, '+').replace(/_/g, '/'));
  const r = new Uint8Array(new ArrayBuffer(b.length));
  for (let i = 0; i < b.length; i++) r[i] = b.charCodeAt(i);
  return r;
}

const mismaClave = (sub: PushSubscription, clave: Uint8Array) => {
  const actual = sub.options.applicationServerKey;
  if (!actual) return false;
  const a = new Uint8Array(actual);
  return a.length === clave.length && a.every((x, i) => x === clave[i]);
};

/** `clave`: la pública VAPID que da `GET /tienda/chat/resumen` (null si el servidor no tiene push). */
export async function estadoPush(clave: string | null): Promise<EstadoPush> {
  if (typeof navigator === 'undefined') return 'no_soportado';
  if (esIphone() && !instalada()) return 'iphone_instalar';
  if (!soporta() || !(await registro())) return 'no_soportado';
  if (!clave) return 'sin_servidor';
  if (Notification.permission === 'denied') return 'bloqueado';
  if (Notification.permission !== 'granted' || leer<boolean>(APAGADO)) return 'apagado';
  return (await (await registro())?.pushManager.getSubscription()) ? 'activo' : 'apagado';
}

/** Suscribe este celular con la clave vigente y se lo dice a la API (si cambió algo desde la última vez). */
async function suscribir(clave: string, sesion: string): Promise<void> {
  const reg = await registro();
  if (!reg) throw new Error('Sin service worker');
  const bytes = bytesDeClave(clave);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !mismaClave(sub, bytes)) {
    await sub.unsubscribe().catch(() => false);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
  const token = JSON.stringify(sub);
  const previo = leer<{ token: string; sesion: string }>(REGISTRADO);
  if (previo?.token === token && previo.sesion === sesion) return;
  await api('/tienda/push', { cuerpo: { token } });
  guardar(REGISTRADO, { token, sesion });
}

/** Pide permiso (tiene que venir de un toque) y suscribe. */
export async function activarPush(clave: string | null, sesion: string): Promise<EstadoPush> {
  const estado = await estadoPush(clave);
  if (estado === 'no_soportado' || estado === 'iphone_instalar' || estado === 'sin_servidor') return estado;
  const permiso = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permiso !== 'granted') return permiso === 'denied' ? 'bloqueado' : 'apagado';
  borrar(APAGADO);
  await suscribir(clave!, sesion);
  return 'activo';
}

export async function desactivarPush(): Promise<void> {
  guardar(APAGADO, true);
  borrar(REGISTRADO);
  const sub = await (await registro())?.pushManager.getSubscription();
  await sub?.unsubscribe().catch(() => false);
  await api('/tienda/push', { metodo: 'DELETE' }).catch(() => undefined);
}

/** Al abrir: si ya dio permiso, renueva la suscripción si hace falta (otra sesión, otra clave). Nunca pide permiso. */
export async function revisarPush(clave: string | null, sesion: string): Promise<void> {
  if (!clave || !soporta() || Notification.permission !== 'granted' || leer<boolean>(APAGADO)) return;
  await suscribir(clave, sesion).catch(() => undefined);
}
